import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, Row, Col, Button, Table, Badge, Spinner, Alert } from 'react-bootstrap';
import GenericButton from '../updated/shared/Button';
import { 
  getOffers, 
  getTrades, 
  getUserProfile,
  refreshWalletData,
  type Offer, 
  type Trade, 
  type UserProfile 
} from '../../services/p2pAPI';
import { getUserWallets } from '../../services/api';
import './P2PDashboard.css';
import IconicHeader from '../updated/shared/IconicHeader';
import P2PNavigation from './P2PNavigation';

interface WalletToken {
  _id: string;
  coinName: string;
  coinSymbol: string;
  coinBalance: number;
  coinBalanceInUSD: number;
  coinNetwork: string;
  coinType: string;
  isCoinActive: boolean;
  coinPrice: number;
}

const P2PDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    allOffers: 0,
    activeOffers: 0,
    completedTrades: 0,
    totalVolume: 0,
  });
  const [recentTrades, setRecentTrades] = useState<Trade[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [connectionError, setConnectionError] = useState<string>('');
  const [walletTokens, setWalletTokens] = useState<WalletToken[]>([]);
  const [walletLoading, setWalletLoading] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Apply theme class based on userType (CaptainBee = orange, normal = green)
  // Match the logic used in IconicHeader: if userType !== 'Indexx Exchange', it's CaptainBee
  useEffect(() => {
    const userType = localStorage.getItem('userType');
    const userLogged = localStorage.getItem('userlogged');
    
    // Determine theme class based on userType or userlogged
    // Match IconicHeader logic: userType !== 'Indexx Exchange' means CaptainBee
    let themeClass = 'theme-normal'; // Default to green
    
    if (userType && userType !== 'Indexx Exchange') {
      // Any userType other than 'Indexx Exchange' is CaptainBee/HoneyUser
      themeClass = 'theme-captain'; // Orange for CaptainBee
    } else if (userType === 'CaptainBee' || userLogged === 'captain') {
      themeClass = 'theme-captain'; // Orange for CaptainBee
    } else if (userType === 'HoneyUser' || userLogged === 'honeyb') {
      themeClass = 'theme-honeyb'; // Orange for HoneyBee
    } else if (userLogged === 'normal' || userType === 'Indexx Exchange') {
      themeClass = 'theme-normal'; // Green for normal users
    }
    
    // Apply theme class to document body and root
    document.body.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.body.classList.add(themeClass);
    document.documentElement.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.documentElement.classList.add(themeClass);
    
    // Also apply to root element to ensure CSS variables are available
    const root = document.getElementById('root');
    if (root) {
      root.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      root.classList.add(themeClass);
    }
    
    // Cleanup on unmount
    return () => {
      // Don't remove theme class on unmount as other components might need it
    };
  }, []);

  // Refresh dashboard when wallet refresh is needed
  useEffect(() => {
    const checkRefresh = () => {
      const refreshNeeded = localStorage.getItem('p2p_dashboard_refresh_needed');
      const walletRefreshNeeded = localStorage.getItem('p2p_wallet_refresh_needed');
      
      if (refreshNeeded || walletRefreshNeeded) {
        console.log('Dashboard refresh triggered by wallet/trade update');
        loadDashboardData();
        localStorage.removeItem('p2p_dashboard_refresh_needed');
        localStorage.removeItem('p2p_wallet_refresh_needed');
      }
    };

    // Check on mount
    checkRefresh();

    // Also listen for storage events (when other tabs update)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'p2p_dashboard_refresh_needed' || e.key === 'p2p_wallet_refresh_needed') {
        console.log('Storage event detected, refreshing dashboard...');
        loadDashboardData();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Check periodically (every 5 seconds) for refresh flag
    const refreshInterval = setInterval(checkRefresh, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(refreshInterval);
    };
  }, []);

  const loadWalletData = async (userEmail: string) => {
    try {
      setWalletLoading(true);
      console.log('Loading wallet data for:', userEmail);
      
      const walletResponse = await getUserWallets(userEmail);
      console.log('Wallet response:', walletResponse);
      
      if (walletResponse.status === 200 && walletResponse.data) {
        // Filter active tokens with balance > 0
        const activeTokens = walletResponse.data.filter((token: any) => 
          token.isCoinActive && token.coinBalance > 0
        );
        
        setWalletTokens(activeTokens);
        console.log('Active wallet tokens:', activeTokens.length);
      } else {
        setWalletTokens([]);
        console.log('No wallet data found');
      }
    } catch (error: any) {
      console.error('Failed to load wallet data:', error);
      setWalletTokens([]);
    } finally {
      setWalletLoading(false);
    }
  };

  const refreshWalletDataAfterTrade = async (userEmail: string) => {
    try {
      console.log('Refreshing wallet data after trade operation...');
      
      // Call the refresh function
      await refreshWalletData(userEmail);
      
      // Reload wallet data
      await loadWalletData(userEmail);
      
      console.log('Wallet data refreshed successfully');
    } catch (error: any) {
      console.error('Failed to refresh wallet data:', error);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setConnectionError('');
      
      const userEmail = localStorage.getItem('user');
      
      if (!userEmail) {
        setConnectionError('User not authenticated. Please log in to access P2P features.');
        return;
      }

      console.log('User email from localStorage:', userEmail);

      // Load wallet data
      loadWalletData(userEmail);

      // Calculate real stats from offers and trades data
      try {
        // Get all offers
        const offersResponse = await getOffers();
        const allOffers = offersResponse.data || [];
        const allOffersCount = allOffers.length;
        const activeOffersGlobal = allOffers.filter((offer: any) => (offer.status || '').toLowerCase() === 'active').length;
        
        // Get user's trades to calculate completed trades and volume
        const tradesResponse = await getTrades({ limit: 200, email: userEmail });
        const allTrades = tradesResponse.data || [];
        
        // Filter trades where user is either buyer or seller
        const userTrades = allTrades.filter((trade: any) => 
          trade.buyerEmail === userEmail || trade.sellerEmail === userEmail
        );
        
        const completedOffersUser = userTrades.filter((trade: any) => (trade.status || '').toLowerCase() === 'completed').length;
        
        // Calculate total volume from user's completed trades (fiat USD)
        const totalVolume = userTrades
          .filter((trade: any) => (trade.status || '').toLowerCase() === 'completed')
          .reduce((sum: number, trade: any) => sum + (Number(trade.amount) * Number(trade.price)), 0);
        
        // Set stats
        setStats({
          allOffers: allOffersCount,
          activeOffers: activeOffersGlobal,
          completedTrades: completedOffersUser,
          totalVolume,
        });
      } catch (statsError) {
        console.log('Failed to calculate real stats, using default values');
        setStats({
          allOffers: 0,
          activeOffers: 0,
          completedTrades: 0,
          totalVolume: 0,
        });
      }

      // Load recent trades (optional)
      try {
        const tradesResponse = await getTrades({ limit: 5, email: userEmail });
        const allRecentTrades = tradesResponse.data || [];
        
        // Filter to show only user's recent trades
        const userRecentTrades = allRecentTrades.filter((trade: any) => 
          trade.buyerEmail === userEmail || trade.sellerEmail === userEmail
        );
        
        setRecentTrades(userRecentTrades);
        console.log('Recent user trades:', userRecentTrades.length);
      } catch (tradesError) {
        console.log('Trades not available, using empty array');
        setRecentTrades([]);
      }

    } catch (error: any) {
      console.error('Failed to load dashboard data:', error);
      if (error.message?.includes('ECONNREFUSED') || error.message?.includes('Network Error')) {
        setConnectionError(error.message || 'Failed to connect to backend');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatVolume = (volume: number) => {
    if (volume >= 1000000) {
      return `$${(volume / 1000000).toFixed(1)}M`;
    } else if (volume >= 1000) {
      return `$${(volume / 1000).toFixed(1)}K`;
    } else {
      return `$${volume.toFixed(0)}`;
    }
  };

  const formatWalletBalance = (balance: number, symbol: string) => {
    // Always show complete decimal values without abbreviations
    if (balance >= 1) {
      return `${balance.toFixed(4)} ${symbol}`;
    } else {
      return `${balance.toFixed(8)} ${symbol}`;
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { variant: 'warning', text: 'Pending' },
      paid: { variant: 'info', text: 'Paid' },
      crypto_sent: { variant: 'primary', text: 'Crypto Sent' },
      completed: { variant: 'success', text: 'Completed' },
      cancelled: { variant: 'danger', text: 'Cancelled' },
      disputed: { variant: 'danger', text: 'Disputed' },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || { 
      variant: 'secondary', 
      text: status 
    };

    return <Badge bg={config.variant}>{config.text}</Badge>;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Helper function to get token logo
  const getTokenLogo = (coinSymbol: string) => {
    try {
      // Normalize the symbol
      const normalizedSymbol = coinSymbol.toUpperCase().trim();
      
      // Handle special cases and variations
      const symbolMap: { [key: string]: string } = {
        'USD': 'USD',
        'USDT': 'USDT',
        'USDC': 'USDC',
        'BTC': 'BTC',
        'ETH': 'ETH',
        'BNB': 'BNB',
        'BUSD': 'BUSD',
        'INEX': 'INEX',
        'IN500': 'IN500',
        'INXC': 'INXC',
        'IUSD': 'IUSD',
        'IUSD+': 'IUSD+',
        'IUSDP': 'IUSDP',
        'ADA': 'ADA',
        'DOT': 'DOT',
        'LINK': 'LINK',
        'MATIC': 'MATIC',
        'SOL': 'SOL',
        'XRP': 'XRP',
        'DOGE': 'DOGE',
        'AVAX': 'AVAX',
        'LTC': 'LTC',
        'CAKE': 'CAKE',
        'AAVE': 'AAVE',
        'SHIB': 'SHIB',
        'TRX': 'TRX',
        'VET': 'VET',
        'CHZ': 'CHZ',
        'MANA': 'MANA',
        'THETA': 'THETA',
        'FTM': 'FTM',
        'ALGO': 'ALCRYP',
        'BCH': 'BCH',
        'XLM': 'XLM',
        'TUSD': 'TUSD',
        'DAI': 'DAI',
        'IBAT': 'IBAT',
        'EPS': 'EPS',
        'LBLOCK': 'LBlock',
      };

      // Get the image name from map or use the symbol directly
      let imageName = symbolMap[normalizedSymbol] || normalizedSymbol;
      
      // Handle special symbols with special characters
      if (normalizedSymbol.includes('+') || normalizedSymbol.includes('-')) {
        imageName = normalizedSymbol.replace(/[+-\s]/g, '');
      }
      
      try {
        // Try loading the image
        return require(`../../assets/token-icons/${imageName}.png`).default;
      } catch (error1) {
        // If that fails, try with the original normalized symbol
        try {
          return require(`../../assets/token-icons/${normalizedSymbol}.png`).default;
        } catch (error2) {
          // Last resort: try common variations
          const variations = [
            normalizedSymbol.replace('+', ''),
            normalizedSymbol.replace('-', ''),
            normalizedSymbol.replace(' ', ''),
          ];
          
          for (const variation of variations) {
            try {
              return require(`../../assets/token-icons/${variation}.png`).default;
            } catch {
              continue;
            }
          }
          
          return null;
        }
      }
    } catch (error) {
      console.error('Error loading token logo for', coinSymbol, ':', error);
      return null;
    }
  };

  if (loading) {
    return (
      <div className="p2p-dashboard-loading">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p>Loading P2P Dashboard...</p>
      </div>
    );
  }

  return (
    <div className="binance-p2p-dashboard">
      <IconicHeader selectedTab="P2P" onChange={() => {}} />
      <P2PNavigation />
      {/* Removed page title header; IconicHeader is used globally on P2P */}
      
      <div className="p2p-content-container">
      {/* User Asset Wallet */}
      <div className="binance-wallet-section">
        <div className="wallet-header">
          <h3>Your Asset Wallet</h3>
          <p>Your current token balances</p>
        </div>
        
        {walletLoading ? (
          <div className="wallet-loading">
            <Spinner animation="border" size="sm" />
            <span className="ms-2">Loading wallet data...</span>
          </div>
        ) : walletTokens.length > 0 ? (
          <div className="wallet-tokens-grid">
            {walletTokens.map((token) => {
              const tokenLogo = getTokenLogo(token.coinSymbol);
              return (
                <div key={token._id} className="wallet-token-card">
                  <div className="token-symbol-badge">
                    <div className="token-icon">
                      {tokenLogo ? (
                        <img 
                          src={tokenLogo} 
                          alt={token.coinSymbol}
                          className="token-logo-image"
                        />
                      ) : (
                        <span className="token-icon-fallback">
                          {token.coinSymbol.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="token-symbol">{token.coinSymbol}</div>
                  </div>
                  <div className="token-amount">
                    {formatWalletBalance(token.coinBalance, token.coinSymbol)}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="wallet-empty">
            <div className="empty-icon">
              <i className="fas fa-wallet"></i>
            </div>
            <h4>No Active Tokens</h4>
            <p>You don't have any active tokens with balance in your wallet.</p>
            <GenericButton
              text="View All Assets"
              onClick={() => window.location.href = '/update/home'}
              IconComponent={() => <i className="fas fa-eye" style={{ marginRight: '8px' }}></i>}
              className=""
              styles={{}}
              disabled={false}
              loading={false}
            />
          </div>
        )}
      </div>

      {/* Binance P2P Quick Stats */}
      <div className="binance-stats-section">
        <div className="stats-container">
          <div className="stat-item">
            <div className="stat-value">{stats.allOffers}</div>
            <div className="stat-label">All Offers</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.activeOffers}</div>
            <div className="stat-label">Active Offers</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{stats.completedTrades}</div>
            <div className="stat-label">Completed Offers</div>
          </div>
          <div className="stat-item">
            <div className="stat-value">{formatVolume(stats.totalVolume)}</div>
            <div className="stat-label">Total Volume (USD)</div>
          </div>
        </div>
      </div>

      {/* Binance P2P Quick Actions */}
      <div className="binance-quick-actions">
        <div className="actions-header">
          <h3>Quick Actions</h3>
          <p>Start trading or manage your offers</p>
        </div>
        <div className="actions-grid">
          <Link to="/p2p/offers" className="action-card">
            <div className="action-icon">
              <i className="fas fa-search"></i>
            </div>
            <div className="action-content">
              <h4>Browse Offers</h4>
              <p>Find the best deals from other traders</p>
            </div>
          </Link>
          
          <Link to="/p2p/trades" className="action-card">
            <div className="action-icon">
              <i className="fas fa-handshake"></i>
            </div>
            <div className="action-content">
              <h4>My Trades</h4>
              <p>Manage your active and completed trades</p>
            </div>
          </Link>
          
          <Link to="/p2p/offers/create" className="action-card">
            <div className="action-icon">
              <i className="fas fa-plus-circle"></i>
            </div>
            <div className="action-content">
              <h4>Create Offer</h4>
              <p>Create a new buy or sell offer</p>
            </div>
          </Link>
        </div>
      </div>

      {/* Binance P2P Recent Trades */}
      <div className="binance-recent-trades">
        <div className="trades-header">
          <div className="trades-header-content">
            <h3>Recent Trades</h3>
            <p>Your latest trading activity</p>
          </div>
          <Link to="/p2p/trades" className="view-all-link">
            View All <i className="fas fa-arrow-right"></i>
          </Link>
        </div>
        
        {recentTrades.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">
              <i className="fas fa-exchange-alt"></i>
            </div>
            <h4>No Recent Trades</h4>
            <p>Start trading to see your transaction history here</p>
            <GenericButton
              text="Start Trading"
              onClick={() => window.location.href = '/p2p/offers'}
              IconComponent={() => null}
              className=""
              styles={{}}
              disabled={false}
              loading={false}
            />
          </div>
        ) : (
          <div className="trades-table">
            <div className="table-header">
              <div className="col-id">Trade ID</div>
              <div className="col-crypto">Crypto</div>
              <div className="col-amount">Amount</div>
              <div className="col-price">Price</div>
              <div className="col-status">Status</div>
              <div className="col-date">Date</div>
              <div className="col-action">Action</div>
            </div>
            
            <div className="table-body">
              {recentTrades.map((trade) => (
                <div key={trade.id} className="trade-row">
                  <div className="col-id">
                    <code>{trade.id.slice(0, 8)}...</code>
                  </div>
                  <div className="col-crypto">
                    {(() => {
                      const tokenLogo = getTokenLogo(trade.cryptoType);
                      return (
                        tokenLogo ? (
                          <img 
                            src={tokenLogo} 
                            alt={trade.cryptoType}
                            className="crypto-icon-image"
                            style={{ width: '24px', height: '24px', borderRadius: '50%' }}
                          />
                        ) : (
                          <span className="crypto-badge">{trade.cryptoType}</span>
                        )
                      );
                    })()}
                  </div>
                  <div className="col-amount">{trade.amount}</div>
                  <div className="col-price">{formatCurrency(trade.price)}</div>
                  <div className="col-status">
                    <span className={`status-badge ${trade.status}`}>
                      {trade.status.replace('_', ' ')}
                    </span>
                  </div>
                  <div className="col-date">{formatDate(trade.createdAt)}</div>
                  <div className="col-action">
                    <GenericButton
                      text="View"
                      onClick={() => window.location.href = `/p2p/trades/${trade.id}`}
                      IconComponent={() => null}
                      className=""
                      styles={{}}
                      disabled={false}
                      loading={false}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Connection Error Alert (Only shown if there's a critical error) */}
      {connectionError && (
        <div className="connection-error-section">
          <Alert variant="danger" className="mt-3">
            <Alert.Heading>Connection Error</Alert.Heading>
            <p>{connectionError}</p>
            <hr />
            <div className="d-flex justify-content-end">
              <GenericButton
                text="Retry Connection"
                onClick={loadDashboardData}
                IconComponent={() => <i className="fas fa-sync-alt" style={{ marginRight: '8px' }}></i>}
                className=""
                styles={{}}
                disabled={false}
                loading={false}
              />
            </div>
          </Alert>
        </div>
      )}
      </div>
    </div>
  );
};

export default P2PDashboard;
